import { useState, useEffect, useCallback, useRef } from 'react'
import './RegistersTab.css'
import { getRegisters, uploadRegister } from '../../api'
import LoadError from '../../components/LoadError'
import { loadList } from '../../utils/loadState'

function fmtDollar(n) {
  if (n == null || isNaN(n)) return '—'
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${Math.round(n / 1000)}K`
  return `$${n.toFixed(2)}`
}

function fmtDate(s) {
  if (!s) return '—'
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return s }
}

function vendorLabel(v) {
  const map = { paycom: 'Paycom', dayforce: 'Dayforce', adp: 'ADP', ukg: 'UKG Pro', paychex: 'Paychex', unknown: 'Auto-detected' }
  return map[v?.toLowerCase()] || v || 'Auto-detected'
}

// ── Upload result card shown immediately after parse ─────────────────────────
function UploadResult({ result, onDismiss }) {
  const ok = result.success
  return (
    <div className={`rg-result-card ${ok ? 'ok' : 'error'}`}>
      <div className="rg-result-header">
        <div className={`rg-result-icon ${ok ? 'ok' : 'error'}`}>{ok ? '✓' : '✕'}</div>
        <div>
          <div className="rg-result-title">
            {ok ? `Register parsed — ${vendorLabel(result.vendor)}` : 'Parse failed'}
          </div>
          <div className="rg-result-sub">
            {ok
              ? `${result.company_name || ''}${result.check_date ? ` · Check date ${result.check_date}` : ''}${result.period_ending ? ` · Period ending ${result.period_ending}` : ''}`
              : result.errors?.[0] || 'Unknown error'}
          </div>
        </div>
        <button className="rg-result-dismiss" onClick={onDismiss}>✕</button>
      </div>
      {ok && (
        <>
          <div className="rg-result-stats">
            <div className="rg-result-stat">
              <div className="rg-result-stat-val">{result.total_employees?.toLocaleString()}</div>
              <div className="rg-result-stat-lbl">Employees</div>
            </div>
            <div className="rg-result-stat">
              <div className={`rg-result-stat-val ${result.validation_pct < 100 ? 'warn' : 'green'}`}>
                {result.validation_pct?.toFixed(1)}%
              </div>
              <div className="rg-result-stat-lbl">Validated</div>
            </div>
            <div className="rg-result-stat">
              <div className="rg-result-stat-val">{fmtDollar(result.total_gross)}</div>
              <div className="rg-result-stat-lbl">Total Gross</div>
            </div>
            <div className="rg-result-stat">
              <div className="rg-result-stat-val">{fmtDollar(result.total_net)}</div>
              <div className="rg-result-stat-lbl">Total Net</div>
            </div>
            <div className="rg-result-stat">
              <div className="rg-result-stat-val">{fmtDollar(result.total_taxes)}</div>
              <div className="rg-result-stat-lbl">Total Taxes</div>
            </div>
            <div className="rg-result-stat">
              <div className="rg-result-stat-val">{fmtDollar(result.total_deductions)}</div>
              <div className="rg-result-stat-lbl">Deductions</div>
            </div>
          </div>
          {result.warnings?.length > 0 && (
            <div className="rg-result-warnings">
              <div className="rg-result-warn-label">⚠ {result.warnings.length} warning{result.warnings.length !== 1 ? 's' : ''}</div>
              {result.warnings.slice(0, 3).map((w, i) => (
                <div key={i} className="rg-result-warn-item">{w}</div>
              ))}
              {result.warnings.length > 3 && (
                <div className="rg-result-warn-item" style={{ color: 'var(--gray-400)' }}>
                  +{result.warnings.length - 3} more
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Individual register row ──────────────────────────────────────────────────
function RegisterRow({ register }) {
  const [expanded, setExpanded] = useState(false)
  const hasVariances = (register.valid_employees < register.total_employees)
  const varianceCount = register.total_employees - register.valid_employees

  return (
    <div className={`rg-row ${expanded ? 'expanded' : ''}`}>
      <div className="rg-row-main" onClick={() => setExpanded(e => !e)}>
        <div className="rg-row-icon">{vendorLabel(register.vendor).slice(0, 3).toUpperCase()}</div>
        <div className="rg-row-body">
          <div className="rg-row-name">
            {register.company_name || 'Register'}
            {register.check_date && <span className="rg-row-date">Check {register.check_date}</span>}
            {register.period_ending && <span className="rg-row-date">Period ending {register.period_ending}</span>}
          </div>
          <div className="rg-row-meta">
            {vendorLabel(register.vendor)}
            {' · '}{register.total_employees?.toLocaleString()} employees
            {' · '}{fmtDollar(register.total_gross)} gross
            {' · '}Uploaded {fmtDate(register.uploaded_at)}
          </div>
        </div>
        <div className="rg-row-right">
          <span className={`rg-val-badge ${hasVariances ? 'warn' : 'ok'}`}>
            {hasVariances ? `${varianceCount} variance${varianceCount !== 1 ? 's' : ''}` : `${register.validation_pct?.toFixed(0)}% valid`}
          </span>
          <span className="rg-row-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {expanded && (
        <div className="rg-row-detail">
          <div className="rg-detail-grid">
            <div className="rg-detail-item"><span className="rg-detail-lbl">Vendor</span><span className="rg-detail-val">{vendorLabel(register.vendor)}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Employees</span><span className="rg-detail-val">{register.total_employees?.toLocaleString()}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Valid</span><span className="rg-detail-val">{register.valid_employees?.toLocaleString()} ({register.validation_pct?.toFixed(1)}%)</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Total Gross</span><span className="rg-detail-val">{fmtDollar(register.total_gross)}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Total Net</span><span className="rg-detail-val">{fmtDollar(register.total_net)}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Taxes</span><span className="rg-detail-val">{fmtDollar(register.total_taxes)}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Deductions</span><span className="rg-detail-val">{fmtDollar(register.total_deductions)}</span></div>
            <div className="rg-detail-item"><span className="rg-detail-lbl">Register ID</span><span className="rg-detail-val" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{register.register_id}</span></div>
          </div>
          {hasVariances && (
            <div className="rg-detail-actions">
              <button className="rg-detail-btn">View Variances →</button>
              <button className="rg-detail-btn">View Employees →</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Drop zone ────────────────────────────────────────────────────────────────
function DropZone({ onFile, uploading }) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef()

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) onFile(file)
  }, [onFile])

  const handleChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (file) onFile(file)
    e.target.value = ''
  }, [onFile])

  return (
    <div
      className={`rg-dropzone ${dragging ? 'drag' : ''} ${uploading ? 'uploading' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !uploading && inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleChange} disabled={uploading} />
      {uploading ? (
        <>
          <div className="rg-spinner" style={{ marginBottom: 10 }} />
          <div className="rg-dz-title">Parsing register…</div>
          <div className="rg-dz-sub">Extracting employees, validating totals</div>
        </>
      ) : (
        <>
          <div className="rg-dz-icon">📄</div>
          <div className="rg-dz-title">{dragging ? 'Drop to upload' : 'Drop payroll register PDF'}</div>
          <div className="rg-dz-sub">or <span className="rg-dz-link">click to browse</span> · PDF only · Max 25MB</div>
          <div className="rg-dz-note">Vendor auto-detected · Supports Paycom, Dayforce, ADP</div>
        </>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RegistersTab({ customerId }) {
  const [registers, setRegisters] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [lastResult, setLastResult] = useState(null)
  // A failed load is an error with a Retry, never "No registers uploaded" (item 201).
  const [loadError, setLoadError] = useState(null)

  async function load() {
    setLoading(true)
    const { items, error } = await loadList(() => getRegisters(customerId), 'registers')
    setRegisters(items || [])
    setLoadError(error)
    setLoading(false)
  }

  useEffect(() => { load() }, [customerId])

  const handleFile = useCallback(async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are accepted. Please upload a payroll register PDF.')
      return
    }
    setUploading(true)
    setLastResult(null)
    try {
      const result = await uploadRegister(file, customerId, null)
      setLastResult(result)
      if (result?.success) {
        const reg = await getRegisters(customerId)
        setRegisters(reg?.registers || [])
      }
    } catch (err) {
      setLastResult({ success: false, errors: [err.message] })
    } finally {
      setUploading(false)
    }
  }, [customerId])

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '40px 0', color: 'var(--gray-400)' }}>
      <div className="rg-spinner" /> Loading registers…
    </div>
  )

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>

      {/* Upload result */}
      {lastResult && (
        <UploadResult result={lastResult} onDismiss={() => setLastResult(null)} />
      )}

      {/* Register list */}
      {loadError ? (
        <LoadError what="registers" error={loadError} onRetry={load} />
      ) : registers.length > 0 ? (
        <div className="rg-list">
          <div className="rg-list-header">
            <span>{registers.length} register{registers.length !== 1 ? 's' : ''}</span>
          </div>
          {registers.map((r, i) => (
            <RegisterRow key={r.register_id || i} register={r} />
          ))}
        </div>
      ) : !lastResult && (
        <div className="rg-empty">
          <div className="rg-empty-icon">📋</div>
          <h3>No registers uploaded</h3>
          <p>Upload a payroll register PDF to validate employee-level gross and net pay calculations.</p>
        </div>
      )}

      {/* Upload zone */}
      <div style={{ marginTop: registers.length > 0 ? 24 : 0 }}>
        <div className="rg-section-title" style={{ marginBottom: 10 }}>
          {registers.length > 0 ? 'Upload Another Register' : 'Upload Register'}
        </div>
        <DropZone onFile={handleFile} uploading={uploading} />
      </div>

    </div>
  )
}
